import TasksApi from 'services/api/tasks-api';
import TasksUtil from 'services/utils/tasks-util';
import { push } from 'react-router-redux';
import {
  baseRoutePath,
  loadSubTradesRequest,
  loadTradesSuccess,
  updateTask,
  updateTaskSuccess,
} from '../work-orders';
import {
  WORK_ORDERS_CREATION_TASKS_STATE_INIT,
  WORK_ORDERS_CREATION_TASKS_TASK_ADD_EMPTY_NEW,
  WORK_ORDERS_CREATION_TASKS_TASK_CANCEL_HIDE,
  WORK_ORDERS_CREATION_TASKS_TASK_CANCEL_NEW,
  WORK_ORDERS_CREATION_TASKS_TASK_CANCEL_SHOW,
  WORK_ORDERS_CREATION_TASKS_TASK_CANCEL_SUCCESS,
  WORK_ORDERS_CREATION_TASKS_TASK_CHANGE_VALUE,
  WORK_ORDERS_CREATION_TASKS_TASK_EDIT,
  WORK_ORDERS_CREATION_TASKS_TASK_IMAGE_REMOVE_SUCCESS,
  WORK_ORDERS_CREATION_TASKS_TASK_SAVE_SUCCESS,
} from '../action-types';
import TradesApi from 'services/api/trades-api';
import { showErrorMessage } from 'actions/messages';
import { goToSubContractorsUnionTrades, goToSummary } from './creation';
import { createEmptyTask } from 'reducers/admin/work-orders/creation/tasks.data';

export function initializeTasksState(
  editTaskId,
  tasks,
  currentTask,
  companyName
) {
  return (dispatch) => {
    // Initialize state based on the current values (it's possible to edit a task from another page)
    let localEditTaskId = editTaskId;
    let localCurrentTask = currentTask;
    if (editTaskId === -1 && tasks.length === 0) {
      localEditTaskId = 0;
      localCurrentTask = createEmptyTask();
    } else if (editTaskId === 0) {
      localCurrentTask = createEmptyTask();
    } else if (
      editTaskId > 0 &&
      tasks.filter((task) => task.id === editTaskId).length === 0
    ) {
      localEditTaskId = 0;
      localCurrentTask = createEmptyTask();
    }

    dispatch({
      type: WORK_ORDERS_CREATION_TASKS_STATE_INIT,
      payload: {
        editTaskId: localEditTaskId,
        currentTask: localCurrentTask,
        companyName: companyName,
      },
    });
  };
}

export function loadTradesRequest() {
  return (dispatch) => {
    return TradesApi.getTrades().then((response) => {
      if (response.ok) {
        dispatch(loadTradesSuccess(response.trades));
      } else {
        dispatch(showErrorMessage(response.errors));
      }

      return response;
    });
  };
}

export function saveTaskRequest(task, workOrderId, isPreTask = false) {
  return (dispatch) => {
    return TasksApi.createTask(task, workOrderId, isPreTask).then(
      (response) => {
        if (response.ok) {
          dispatch(saveTaskSuccess(response.newTask));
        } else {
          dispatch(showErrorMessage(response.errors));
        }

        return response;
      }
    );
  };
}

export function saveTaskSuccess(newTask) {
  return {
    type: WORK_ORDERS_CREATION_TASKS_TASK_SAVE_SUCCESS,
    payload: newTask,
  };
}

export function updateTaskRequest(task) {
  return (dispatch) => {
    return TasksApi.updateTask(task).then((response) => {
      if (response.ok) {
        dispatch(updateTaskSuccess());
        dispatch(updateTask(response.task));
      } else {
        dispatch(showErrorMessage(response.errors));
      }
    });
  };
}

export function addEmptyTask(tasks) {
  return {
    type: WORK_ORDERS_CREATION_TASKS_TASK_ADD_EMPTY_NEW,
    payload: tasks,
  };
}

export function editTask(taskId, tasks) {
  const task = tasks.find((element) => element.id === taskId);
  return (dispatch) => {
    dispatch({
      type: WORK_ORDERS_CREATION_TASKS_TASK_EDIT,
      payload: {
        taskId,
        task: TasksUtil.mapTask(task),
      },
    });

    // Load sub trades in case not loaded yet (could happen when editing tasks)
    dispatch(loadSubTradesRequest(task.trade.id));
  };
}

export function cancelTaskRequest(id) {
  if (id === 0) {
    // Task is not saved yet, don't call backend
    return (dispatch) => {
      dispatch(cancelNewTask());
    };
  } else {
    return (dispatch) => {
      return TasksApi.removeTask(id).then((response) => {
        if (response.ok) {
          dispatch(cancelTaskSuccess(id));
        } else {
          dispatch(showErrorMessage(response.errors));
        }
      });
    };
  }
}

export function cancelNewTask() {
  return {
    type: WORK_ORDERS_CREATION_TASKS_TASK_CANCEL_NEW,
  };
}

export function cancelTaskSuccess(id) {
  return {
    type: WORK_ORDERS_CREATION_TASKS_TASK_CANCEL_SUCCESS,
    payload: id,
  };
}

export function cancelTaskShow() {
  return {
    type: WORK_ORDERS_CREATION_TASKS_TASK_CANCEL_SHOW,
  };
}

export function cancelTaskHide() {
  return {
    type: WORK_ORDERS_CREATION_TASKS_TASK_CANCEL_HIDE,
  };
}

export function currentTaskChangeRequest(taskId, attribute, value, label) {
  return (dispatch) => {
    if (attribute === 'images') {
      // If new files uploaded, read them first so it's possible to show with a preview

      let imageLoaded = [];
      let imageLoaderPromises = value.map((image) => {
        return new Promise((resolve) => {
          let reader = new FileReader();
          reader.onloadend = () => {
            imageLoaded.push({
              id: 0,
              file: {
                original: image,
                img: reader.result,
              },
              url: null,
            });
            resolve();
          };
          reader.readAsDataURL(image);
        });
      });

      Promise.all(imageLoaderPromises).then(() => {
        dispatch(currentTaskChange(taskId, attribute, imageLoaded));
      });
    } else {
      dispatch(currentTaskChange(taskId, attribute, value, label));
    }
  };
}

export function currentTaskChange(taskId, attribute, value, label) {
  return {
    type: WORK_ORDERS_CREATION_TASKS_TASK_CHANGE_VALUE,
    payload: {
      taskId: taskId,
      attribute: attribute,
      value: value,
      label: label,
    },
  };
}

export function taskContinue(newWOProcessStarted) {
  return (dispatch) => {
    if (newWOProcessStarted) {
      dispatch(goToSubContractorsUnionTrades());
    } else {
      dispatch(goToSummary());
    }
  };
}

export function taskBack() {
  return (dispatch) => {
    dispatch(push(baseRoutePath + '/new-work-order'));
  };
}

export function removeImageRequest(currentTask, imageIndex) {
  return (dispatch) => {
    const image = currentTask.images[imageIndex];
    if (image.id === 0) {
      // Image is local
      dispatch(removeImage(imageIndex));
    } else {
      // Image is stored in the backend, removeUser from there
      return TasksApi.removeImage(currentTask.id, image.id).then((response) => {
        if (response.ok) {
          dispatch(removeImage(imageIndex));
          dispatch(updateTask(response.task));
        } else {
          dispatch(showErrorMessage(response.errors));
        }
      });
    }
  };
}

export function removeImage(imageIndex) {
  return {
    type: WORK_ORDERS_CREATION_TASKS_TASK_IMAGE_REMOVE_SUCCESS,
    payload: imageIndex,
  };
}



// WEBPACK FOOTER //
// ./src/actions/admin/work-orders/creation/tasks.js